import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIsAdmin } from '@hooks/usePermission';
import { useLocation, useHistory } from 'react-router-dom';

import { useEmployeeListRoster } from '../../../hooks/useEmployeeListRoster';
import {
  getSalesforceAuthUrl,
  disconnectSalesforce,
  autoMapSalesforceEmployees,
  exportAllEmployeesToSalesforce,
  getSalesforceContacts,
  createAndMapSalesforceEmployee,
  saveSalesforceMappings,
  syncSalesforceTimeRange,
  importSalesforceEmployees,
  previewSalesforceImport,
  subscribeToSFStatus,
  subscribeToSFImportLogs,
  getSFImportLogDetails,
} from '../../../services/salesforce';
import { ImportPreviewModal } from './ImportPreviewModal.component';
import {
  addNotification,
  notificationStates,
} from '../../../components/Notification';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { Button, buttonVariants, buttonSizes } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import styles from './QuickBooksCard.module.scss'; // reuse QB card styles — identical layout

const SF_CONNECTED_PARAM = 'sf_connected';
const SF_ERROR_PARAM = 'sf_error';

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function firstOfMonthStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

const ERROR_MESSAGES = {
  access_denied: 'Salesforce authorization was cancelled.',
  missing_params: 'Authorization failed: missing parameters from Salesforce.',
  invalid_state: 'Authorization failed: invalid or missing session state.',
  state_expired: 'Authorization session expired. Please try again.',
  server_error:
    'A server error occurred during authorization. Please try again.',
};

function fmtDate(dateObj) {
  if (!dateObj) return '—';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(dateObj);
}

export const SalesforceCard = () => {
  const userProfile = useSelector((state) => state.auth.profile);
  const activeTeamId = useSelector(
    (s) => s.team?.active?.activeTeamid ?? s.team?.active?.activeTeamId
  );
  const location = useLocation();
  const history = useHistory();

  const companyId = userProfile?.activeCompany?.companyId;
  const userId = userProfile?.id;
  const isAdmin = useIsAdmin();
  const teamId = activeTeamId;

  const { employees: prodaffEmployees, isLoading: rosterLoading } =
    useEmployeeListRoster(teamId);

  const [sfStatus, setSfStatus] = useState({
    connected: false,
    instanceUrl: null,
    connectedAt: null,
    connectedBy: null,
    employeeMappings: {},
    employeeMappingsUpdatedAt: null,
    lastSyncAt: null,
    lastSyncStats: null,
  });

  const [sfContacts, setSfContacts] = useState([]);
  const [sfContactsLoading, setSfContactsLoading] = useState(false);
  const [pendingMappings, setPendingMappings] = useState({});

  const [syncRange, setSyncRange] = useState({
    start: firstOfMonthStr(),
    end: todayStr(),
  });
  const [syncing, setSyncing] = useState(false);

  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [autoMapping, setAutoMapping] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);
  const [savingMappings, setSavingMappings] = useState(false);
  const [creatingFor, setCreatingFor] = useState(null);
  const [importing, setImporting] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [importResults, setImportResults] = useState(null);

  const [historyLogs, setHistoryLogs] = useState([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsTitle, setDetailsTitle] = useState('');

  // ── OAuth redirect handling ─────────────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const connected = params.get(SF_CONNECTED_PARAM);
    const errorCode = params.get(SF_ERROR_PARAM);

    if (!connected && !errorCode) return;

    if (connected === 'true') {
      addNotification({
        message: 'Salesforce connected successfully.',
        level: notificationStates.success,
      });
    } else if (errorCode) {
      addNotification({
        message:
          ERROR_MESSAGES[errorCode] || 'Salesforce authorization failed.',
        level: notificationStates.error,
      });
    }

    params.delete(SF_CONNECTED_PARAM);
    params.delete(SF_ERROR_PARAM);
    const cleanSearch = params.toString();
    history.replace({
      pathname: location.pathname,
      search: cleanSearch ? `?${cleanSearch}` : '',
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Firestore real-time status ──────────────────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    const unsub = subscribeToSFStatus(companyId, setSfStatus);
    return unsub;
  }, [companyId]);

  // ── Import history subscription ─────────────────────────────────────────────
  useEffect(() => {
    if (!companyId || !sfStatus.connected) return;
    const unsub = subscribeToSFImportLogs(companyId, setHistoryLogs);
    return unsub;
  }, [companyId, sfStatus.connected]);

  // ── Load SF contacts when connected ────────────────────────────────────────
  useEffect(() => {
    if (!sfStatus.connected || !companyId) {
      setSfContacts([]);
      return;
    }
    setSfContactsLoading(true);
    getSalesforceContacts(companyId)
      .then(({ contacts }) => setSfContacts(contacts || []))
      .catch(() =>
        addNotification({
          message: 'Failed to load Salesforce contacts.',
          level: notificationStates.error,
        })
      )
      .finally(() => setSfContactsLoading(false));
  }, [sfStatus.connected, companyId]);

  const effectiveMappings = useMemo(() => {
    const base = sfStatus.employeeMappings || {};
    const merged = { ...base };
    for (const [uid, val] of Object.entries(pendingMappings)) {
      if (val === null) delete merged[uid];
      else merged[uid] = val;
    }
    return merged;
  }, [sfStatus.employeeMappings, pendingMappings]);

  const hasPendingChanges = Object.keys(pendingMappings).length > 0;

  const importPreview = useMemo(() => {
    if (!sfContacts.length) return null;
    const mappedSfIds = new Set(
      Object.values(effectiveMappings).map((m) => m.sfContactId)
    );
    const prodaffEmails = new Set(
      prodaffEmployees.map((e) => e.email?.toLowerCase()).filter(Boolean)
    );
    let toCreate = 0,
      toLink = 0,
      toSkip = 0,
      noEmail = 0;
    for (const c of sfContacts) {
      if (mappedSfIds.has(c.id)) {
        toSkip++;
        continue;
      }
      if (!c.email) {
        noEmail++;
        continue;
      }
      if (prodaffEmails.has(c.email)) toLink++;
      else toCreate++;
    }
    return { toCreate, toLink, toSkip, noEmail };
  }, [sfContacts, effectiveMappings, prodaffEmployees]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleConnect = useCallback(async () => {
    if (!isAdmin) {
      addNotification({
        message: 'Only administrators can connect integrations.',
        level: notificationStates.error,
      });
      return;
    }
    setConnecting(true);
    try {
      const { authUrl } = await getSalesforceAuthUrl(companyId, userId);
      window.location.href = authUrl;
    } catch {
      addNotification({
        message:
          'Failed to initiate Salesforce authorization. Please try again.',
        level: notificationStates.error,
      });
      setConnecting(false);
    }
  }, [companyId, userId, isAdmin]);

  const handleDisconnect = useCallback(async () => {
    if (!isAdmin) {
      addNotification({
        message: 'Only administrators can disconnect integrations.',
        level: notificationStates.error,
      });
      return;
    }
    setDisconnecting(true);
    try {
      await disconnectSalesforce(companyId, userId);
      setPendingMappings({});
      addNotification({
        message: 'Salesforce disconnected.',
        level: notificationStates.success,
      });
    } catch {
      addNotification({
        message: 'Failed to disconnect Salesforce. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setDisconnecting(false);
    }
  }, [companyId, userId, isAdmin]);

  const handleAutoMap = useCallback(async () => {
    setAutoMapping(true);
    try {
      const { mapped, unmatched } = await autoMapSalesforceEmployees(
        companyId,
        teamId
      );
      setPendingMappings({});
      const total = mapped + (unmatched?.length ?? 0);
      addNotification({
        message: `Auto-mapped ${mapped} of ${total} employees by email.`,
        level:
          mapped > 0 ? notificationStates.success : notificationStates.warning,
      });
    } catch {
      addNotification({
        message: 'Auto-mapping failed. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setAutoMapping(false);
    }
  }, [companyId, teamId]);

  const handleExportAll = useCallback(async () => {
    setExportingAll(true);
    try {
      const { mapped, created, skipped, errors } =
        await exportAllEmployeesToSalesforce(companyId, teamId);
      setPendingMappings({});
      const parts = [];
      if (created > 0) parts.push(`${created} created`);
      if (mapped > 0) parts.push(`${mapped} matched by email`);
      if (skipped > 0) parts.push(`${skipped} already mapped`);
      if (errors?.length > 0) parts.push(`${errors.length} failed`);
      addNotification({
        message: `Export complete: ${parts.join(', ') || 'nothing to do'}.`,
        level:
          errors?.length > 0
            ? notificationStates.warning
            : notificationStates.success,
      });
    } catch {
      addNotification({
        message: 'Failed to export employees. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setExportingAll(false);
    }
  }, [companyId, teamId]);

  const handleSelectMapping = useCallback(
    (prodaffUserId, sfContactId) => {
      if (!sfContactId) {
        setPendingMappings((prev) => ({ ...prev, [prodaffUserId]: null }));
        return;
      }
      const contact = sfContacts.find((c) => c.id === sfContactId);
      if (!contact) return;
      setPendingMappings((prev) => ({
        ...prev,
        [prodaffUserId]: {
          sfContactId: contact.id,
          sfContactName: contact.name,
        },
      }));
    },
    [sfContacts]
  );

  const handleUnmap = useCallback((prodaffUserId) => {
    setPendingMappings((prev) => ({ ...prev, [prodaffUserId]: null }));
  }, []);

  const handleSaveMappings = useCallback(async () => {
    if (!hasPendingChanges) return;
    setSavingMappings(true);
    try {
      await saveSalesforceMappings(companyId, pendingMappings);
      setPendingMappings({});
      addNotification({
        message: 'Employee mappings saved.',
        level: notificationStates.success,
      });
    } catch {
      addNotification({
        message: 'Failed to save mappings. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setSavingMappings(false);
    }
  }, [companyId, pendingMappings, hasPendingChanges]);

  const handleCreateAndMap = useCallback(
    async (emp) => {
      setCreatingFor(emp.userId);
      try {
        await createAndMapSalesforceEmployee(
          companyId,
          emp.userId,
          emp.name,
          emp.email
        );
        addNotification({
          message: `${emp.name} created in Salesforce and mapped.`,
          level: notificationStates.success,
        });
      } catch {
        addNotification({
          message: `Failed to create ${emp.name} in Salesforce. Please try again.`,
          level: notificationStates.error,
        });
      } finally {
        setCreatingFor(null);
      }
    },
    [companyId]
  );

  const handleOpenImportPreview = useCallback(async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);
    setImportResults(null);
    try {
      const data = await previewSalesforceImport(companyId, teamId, userId);
      setPreviewData(data);
    } catch {
      addNotification({
        message: 'Failed to load import preview. Please try again.',
        level: notificationStates.error,
      });
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  }, [companyId, teamId, userId]);

  const handleClosePreviewModal = useCallback(() => {
    if (importing) return;
    setPreviewOpen(false);
    setPreviewData(null);
    setImportResults(null);
  }, [importing]);

  const handleConfirmImport = useCallback(async () => {
    setImporting(true);
    try {
      const result = await importSalesforceEmployees(companyId, teamId, userId);
      setImportResults(result); // switch modal to results mode
    } catch {
      addNotification({
        message: 'Failed to import from Salesforce. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setImporting(false);
    }
  }, [companyId, teamId, userId]);

  const handleViewDetails = useCallback(
    async (log) => {
      setDetailsLoading(true);
      setDetailsOpen(true);
      setDetailsData(null);
      setDetailsTitle(`SF Import · ${fmtDate(log.importedAt)}`);
      try {
        const detail = await getSFImportLogDetails(companyId, log.id);
        setDetailsData({
          ...log.stats,
          employeeResults: detail.employees || [],
        });
      } catch {
        addNotification({
          message: 'Failed to load import details.',
          level: notificationStates.error,
        });
        setDetailsOpen(false);
      } finally {
        setDetailsLoading(false);
      }
    },
    [companyId]
  );

  const handleSync = useCallback(async () => {
    if (!syncRange.start || !syncRange.end) {
      addNotification({
        message: 'Please select a valid date range.',
        level: notificationStates.error,
      });
      return;
    }
    setSyncing(true);
    try {
      const result = await syncSalesforceTimeRange(
        companyId,
        teamId,
        syncRange.start,
        syncRange.end
      );
      addNotification({
        message: `Sync complete. ${result.synced ?? 0} records synced.`,
        level: notificationStates.success,
      });
    } catch {
      addNotification({
        message: 'Sync failed. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setSyncing(false);
    }
  }, [companyId, teamId, syncRange]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <ImportPreviewModal
        isOpen={previewOpen}
        onClose={handleClosePreviewModal}
        onConfirm={handleConfirmImport}
        preview={previewData}
        loading={previewLoading}
        confirming={importing}
        source="Salesforce"
        results={importResults}
      />
      <ImportPreviewModal
        isOpen={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setDetailsData(null);
        }}
        preview={null}
        loading={detailsLoading}
        confirming={false}
        source="Salesforce"
        results={detailsData}
        resultsTitle={detailsTitle}
      />
      {/* Connection status panel */}
      <div className={styles.statusPanel}>
        <div className={styles.statusLeft}>
          <span
            className={`${styles.badge} ${sfStatus.connected ? styles.connected : styles.disconnected}`}
          >
            {sfStatus.connected ? 'Connected' : 'Not Connected'}
          </span>
          {sfStatus.connected ? (
            <span className={styles.connectedMeta}>
              {sfStatus.instanceUrl &&
                `Org: ${sfStatus.instanceUrl.replace('https://', '')}`}
              {sfStatus.connectedAt &&
                ` · Connected ${fmtDate(sfStatus.connectedAt)}`}
            </span>
          ) : (
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              color="gray-500"
            >
              Connect your Salesforce account to start syncing employee data.
            </Text>
          )}
        </div>
        {isAdmin &&
          (sfStatus.connected ? (
            <Button
              text={disconnecting ? 'Disconnecting…' : 'Disconnect'}
              variant={buttonVariants.secondary}
              size={buttonSizes.sm}
              onClick={handleDisconnect}
              disabled={disconnecting}
            />
          ) : (
            <Button
              text={connecting ? 'Redirecting…' : 'Connect Salesforce'}
              variant={buttonVariants.primary}
              size={buttonSizes.sm}
              onClick={handleConnect}
              disabled={connecting}
            />
          ))}
      </div>

      {/* Employee Mapping panel */}
      {sfStatus.connected && (
        <div className={styles.panel}>
          <div className={styles.sectionHead}>
            <div className={styles.sectionTitleBlock}>
              <span className={styles.sectionTitle}>Employee Mapping</span>
              <span className={styles.sectionSubtitle}>
                Map Prodaff employees to their Salesforce Contact records.
              </span>
            </div>
            <div className={styles.sectionActions}>
              <Button
                text={autoMapping ? 'Mapping…' : 'Auto-map by Email'}
                variant={buttonVariants.secondary}
                size={buttonSizes.sm}
                onClick={handleAutoMap}
                disabled={
                  autoMapping || exportingAll || importing || sfContactsLoading
                }
              />
              <div className={styles.importAction}>
                <Button
                  text={importing ? 'Importing…' : 'Import from Salesforce'}
                  variant={buttonVariants.primary}
                  size={buttonSizes.sm}
                  onClick={handleOpenImportPreview}
                  disabled={
                    importing ||
                    autoMapping ||
                    exportingAll ||
                    sfContactsLoading
                  }
                />
                {importPreview && (
                  <span className={styles.importPreviewHint}>
                    {importPreview.toCreate > 0 &&
                      `${importPreview.toCreate} new`}
                    {importPreview.toCreate > 0 &&
                      importPreview.toLink > 0 &&
                      ' · '}
                    {importPreview.toLink > 0 && `${importPreview.toLink} link`}
                  </span>
                )}
              </div>
              <Button
                text={exportingAll ? 'Exporting…' : 'Export All to Salesforce'}
                variant={buttonVariants.secondary}
                size={buttonSizes.sm}
                onClick={handleExportAll}
                disabled={
                  exportingAll || autoMapping || importing || sfContactsLoading
                }
              />
            </div>
          </div>

          <div className={styles.sectionBody}>
            {rosterLoading || sfContactsLoading ? (
              <Text
                variant={textVariantsEnum.Text_13}
                fontWeight={fontWeightEnum.Normal}
                color="gray-500"
              >
                Loading…
              </Text>
            ) : prodaffEmployees.length === 0 ? (
              <EmptyState
                illustration
                title="No Employees Found"
                description="No employees found in this department to map to Salesforce."
              />
            ) : (
              <>
                <table className={styles.mappingTable}>
                  <thead>
                    <tr>
                      <th>Prodaff Employee</th>
                      <th>Salesforce Contact</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prodaffEmployees.map((emp) => {
                      const mapping = effectiveMappings[emp.userId];
                      const isCreating = creatingFor === emp.userId;

                      return (
                        <tr key={emp.userId}>
                          <td>
                            <div className={styles.employeeName}>
                              {emp.name}
                            </div>
                            <div className={styles.employeeEmail}>
                              {emp.email}
                            </div>
                          </td>
                          <td>
                            {mapping ? (
                              <div className={styles.mappingCell}>
                                <span className={styles.mappedTag}>
                                  {mapping.sfContactName || mapping.sfContactId}
                                  <button
                                    className={styles.unmapBtn}
                                    onClick={() => handleUnmap(emp.userId)}
                                    title="Remove mapping"
                                  >
                                    ×
                                  </button>
                                </span>
                              </div>
                            ) : (
                              <select
                                className={styles.qbSelect}
                                value=""
                                onChange={(e) =>
                                  handleSelectMapping(
                                    emp.userId,
                                    e.target.value
                                  )
                                }
                                disabled={sfContacts.length === 0}
                              >
                                <option value="">— Select SF contact —</option>
                                {sfContacts.map((c) => (
                                  <option key={c.id} value={c.id}>
                                    {c.name}
                                    {c.email ? ` (${c.email})` : ''}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td>
                            <div className={styles.actionCell}>
                              {!mapping && (
                                <button
                                  className={styles.createBtn}
                                  onClick={() => handleCreateAndMap(emp)}
                                  disabled={isCreating}
                                >
                                  {isCreating ? 'Creating…' : '+ Create in SF'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className={styles.saveMappingsRow}>
                  {hasPendingChanges && (
                    <span className={styles.pendingNote}>Unsaved changes</span>
                  )}
                  <Button
                    text={savingMappings ? 'Saving…' : 'Save Mappings'}
                    variant={buttonVariants.primary}
                    size={buttonSizes.sm}
                    onClick={handleSaveMappings}
                    disabled={savingMappings || !hasPendingChanges}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Import History panel */}
      {sfStatus.connected && historyLogs.length > 0 && (
        <div className={styles.panel}>
          <div className={styles.sectionHead}>
            <div className={styles.sectionTitleBlock}>
              <span className={styles.sectionTitle}>Import History</span>
              <span className={styles.sectionSubtitle}>
                Last {historyLogs.length} import run
                {historyLogs.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          <div className={styles.sectionBody}>
            {historyLogs.map((log) => (
              <div key={log.id} className={styles.historyRow}>
                <div className={styles.historyInfo}>
                  <span className={styles.historyDate}>
                    {fmtDate(log.importedAt)}
                  </span>
                  <span className={styles.historyMeta}>
                    by{' '}
                    {log.importedBy?.name ||
                      log.importedBy?.userId ||
                      'Unknown'}
                  </span>
                </div>
                <div className={styles.historyStats}>
                  {(log.stats?.imported || 0) > 0 && (
                    <span className={`${styles.chip} ${styles.chipGreen}`}>
                      {log.stats.imported} created
                    </span>
                  )}
                  {(log.stats?.invited || 0) > 0 && (
                    <span className={`${styles.chip} ${styles.chipBlue}`}>
                      {log.stats.invited} invited
                    </span>
                  )}
                  {(log.stats?.mapped || 0) > 0 && (
                    <span className={`${styles.chip} ${styles.chipGray}`}>
                      {log.stats.mapped} linked
                    </span>
                  )}
                  {(log.stats?.errors || 0) > 0 && (
                    <span className={`${styles.chip} ${styles.chipRed}`}>
                      {log.stats.errors} failed
                    </span>
                  )}
                  {(log.stats?.imported || 0) === 0 &&
                    (log.stats?.invited || 0) === 0 &&
                    (log.stats?.mapped || 0) === 0 &&
                    (log.stats?.errors || 0) === 0 && (
                      <span className={`${styles.chip} ${styles.chipGray}`}>
                        nothing to import
                      </span>
                    )}
                </div>
                <Button
                  text="Details"
                  variant={buttonVariants.secondary}
                  size={buttonSizes.sm}
                  onClick={() => handleViewDetails(log)}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sync panel */}
      {sfStatus.connected && (
        <div className={styles.panel}>
          <div className={styles.sectionHead}>
            <div className={styles.sectionTitleBlock}>
              <span className={styles.sectionTitle}>Sync Time Data</span>
              <span className={styles.sectionSubtitle}>
                Export tracked hours to Salesforce as Prodaff Work Log records
                for a selected date range.
              </span>
            </div>
          </div>

          <div className={styles.sectionBody}>
            <div className={styles.syncRow}>
              <div className={styles.dateField}>
                <label htmlFor="sf-start-date">From</label>
                <input
                  id="sf-start-date"
                  type="date"
                  value={syncRange.start}
                  max={syncRange.end || todayStr()}
                  onChange={(e) =>
                    setSyncRange((r) => ({ ...r, start: e.target.value }))
                  }
                  disabled={syncing}
                />
              </div>
              <div className={styles.dateField}>
                <label htmlFor="sf-end-date">To</label>
                <input
                  id="sf-end-date"
                  type="date"
                  value={syncRange.end}
                  min={syncRange.start}
                  max={todayStr()}
                  onChange={(e) =>
                    setSyncRange((r) => ({ ...r, end: e.target.value }))
                  }
                  disabled={syncing}
                />
              </div>
              <Button
                text={syncing ? 'Syncing…' : 'Sync Now'}
                variant={buttonVariants.primary}
                size={buttonSizes.sm}
                onClick={handleSync}
                disabled={syncing || !syncRange.start || !syncRange.end}
              />
            </div>

            {sfStatus.lastSyncAt && (
              <div className={styles.syncStats}>
                <div className={styles.syncStatsTitle}>
                  Last sync · {fmtDate(sfStatus.lastSyncAt)}
                  {sfStatus.lastSyncStats?.startDate &&
                    sfStatus.lastSyncStats?.endDate && (
                      <span style={{ fontWeight: 400, color: '#64748b' }}>
                        {' '}
                        ({sfStatus.lastSyncStats.startDate} →{' '}
                        {sfStatus.lastSyncStats.endDate})
                      </span>
                    )}
                </div>
                {sfStatus.lastSyncStats && (
                  <div className={styles.syncStatsGrid}>
                    <div className={styles.syncStatItem}>
                      <span className={styles.statLabel}>Synced</span>
                      <span className={`${styles.statValue} ${styles.success}`}>
                        {sfStatus.lastSyncStats.synced ?? 0}
                      </span>
                    </div>
                    <div className={styles.syncStatItem}>
                      <span className={styles.statLabel}>Skipped</span>
                      <span className={`${styles.statValue} ${styles.warning}`}>
                        {sfStatus.lastSyncStats.skipped ?? 0}
                      </span>
                    </div>
                    <div className={styles.syncStatItem}>
                      <span className={styles.statLabel}>Errors</span>
                      <span className={`${styles.statValue} ${styles.error}`}>
                        {sfStatus.lastSyncStats.errors ?? 0}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
